import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { authState, userStorageKey } from '../../api';
import { useNavigate } from 'react-router-dom';
import { loadBalancerAPI } from '../../api/load-balancer';
import { CircularProgress, Grid, Skeleton } from '@mui/material';
import './AnalyticsStyle.css';
import { GlobalNetwork } from './components/GlobalNetwork/GlobalNetwork';
import { abelAPI } from '../../api/abel';
import { AllPoolSummary } from './components/PoolSummary/AllPoolSummary';
import { PoolSummary } from './components/Pools/PoolSummary';

export function Analytics() {
  const navigate = useNavigate();
  const [auth, setAuth] = useRecoilState(authState);
  const [refreshedToken, setRefreshedToken] = useState(false);
  const authStateValue = useRecoilValue(authState);
  const [modalCompleteAndLoading, setModalCompleteAndLoading] = useState({
    modalsCompleted: false,
    isStatusLoading: true,
  });

  const refreshToken = async () => {
    try {
      const auth = localStorage.getItem(userStorageKey);
      const parsedAuth = JSON.parse(auth || '{}');
      if (!parsedAuth.refreshToken) {
        throw new Error('No refresh token available');
      }

      const { data } = await loadBalancerAPI.apiInstance.refreshToken(parsedAuth.refreshToken);
      setAuth((prevState) => ({
        ...prevState,
        accessToken: data.data.accessToken,
        refreshToken: data.data.refreshToken,
        isLoading: false,
      }));
      setRefreshedToken(true);
    } catch (error) {
      console.error('Failed to refresh token:', error);
    }
  };

  const fetchStatus = async () => {
    let isModalComplete: boolean;
    if (!authStateValue?.user?.id) return;
    try {
      const response = await loadBalancerAPI.apiInstance.getTwoFAState(authStateValue?.user?.id);
      if (response?.data) {
        const { twoFAState } = response.data;
        if (twoFAState === false) {
          isModalComplete = true;
          navigate('/analytics');
        } else {
          const statusResponse = await loadBalancerAPI.apiInstance.getTwoFactorStatus();
          const { otpEnabled, otpVerified, otpExpireAt } = statusResponse.data.data || {};

          if (otpEnabled) {
            const expireDate = new Date(otpExpireAt);
            const today = new Date();
            isModalComplete = otpVerified && expireDate > today;
          } else {
            isModalComplete = false;
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setModalCompleteAndLoading((prevState) => ({
        modalsCompleted: isModalComplete ?? prevState.modalsCompleted,
        isStatusLoading: false,
      }));
    }
  };

  useEffect(() => {
    const auth = localStorage.getItem(userStorageKey);
    const checkAuthStatus = async () => {
      if (!auth) {
        navigate('/auth/login');
      } else if (authStateValue.user?.role === 0 || authStateValue.user?.role === 2) {
        navigate('/home');
      } else {
        await fetchStatus();
      }
    };
    checkAuthStatus();
  }, [authStateValue?.user?.role, navigate]);

  useEffect(() => {
    if (!modalCompleteAndLoading.isStatusLoading && !modalCompleteAndLoading.modalsCompleted) {
      navigate('/two-factor-auth', { state: { redirectTo: '/analytics' } });
    }
  }, [modalCompleteAndLoading, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshToken();
      fetchStatus();
      if (!modalCompleteAndLoading.modalsCompleted) {
        navigate('/two-factor-auth', { state: { redirectTo: '/analytics' } });
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [modalCompleteAndLoading.modalsCompleted, navigate]);

  const {
    data: dataNetwork,
    isLoading: isLoadingDataNetwork,
    error: errorDataNetwork,
  } = abelAPI.hookWrapper.useSummaryInfo({
    enabled: modalCompleteAndLoading.modalsCompleted,
  });

  const {
    data: dataPoolSummary,
    isLoading: isLoadingPoolSummary,
    error: errorPoolSummary,
  } = loadBalancerAPI.hookWrapperQuery.usePoolSummaryInfo({
    enabled: modalCompleteAndLoading.modalsCompleted,
  });

  if (modalCompleteAndLoading.isStatusLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (!modalCompleteAndLoading.modalsCompleted) {
    return null;
  }

  return (
    <Grid container spacing={2} className="analytics-page">
      <Grid item xs={12}>
        <GlobalNetwork
          globalNetworkInfo={dataNetwork}
          globalNetworkInfoLoading={isLoadingDataNetwork}
          globalNetworkInfoError={errorDataNetwork}
        />
      </Grid>
      <Grid item xs={12}>
        <AllPoolSummary
          poolSummaryInfo={dataPoolSummary}
          poolSummaryInfoLoading={isLoadingPoolSummary}
          poolSummaryInfoError={errorPoolSummary}
        />
      </Grid>
      {isLoadingPoolSummary ? (
        <Grid item xs={12}>
          <Skeleton variant="rectangular" sx={{ minHeight: '82px', borderRadius: 1 }} />
        </Grid>
      ) : errorPoolSummary || !dataPoolSummary.poolsSummary || !dataPoolSummary.poolsSummary.length ? (
        <></>
      ) : (
        dataPoolSummary.poolsSummary.map((miningPool) => (
          <Grid item xs={12} key={miningPool.name}>
            <PoolSummary poolInfo={miningPool} />
          </Grid>
        ))
      )}
    </Grid>
  );
}

export default Analytics;
